import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreVertical, Eye, AlertCircle, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";

interface Activity {
  id: string;
  user_id: string;
  gym_id: string;
  action: string;
  target: string;
  target_type?: string;
  target_id?: string;
  target_role?: string;
  full_name?: string;
  created_at: string;
  class_name?: string;
  booking_date?: string;
  class_time?: string;
}

interface ActivityTableProps {
  activities: Activity[];
  gyms: Array<{ id: string; name: string; city: string }>;
  onViewDetails: (activity: Activity) => void;
}

export function ActivityTable({ activities, gyms, onViewDetails }: ActivityTableProps) {
  const { t } = useTranslation(["superadmin", "common"]);
  const isMobile = useIsMobile();
  
  const getGymInfo = (gymId: string) => {
    return gyms.find((g) => g.id === gymId);
  };

  const getActionBadgeVariant = (action: string): "default" | "secondary" | "destructive" | "outline" => {
    if (action.includes("created")) return "default";
    if (action.includes("updated")) return "secondary";
    if (action.includes("deleted") || action.includes("cancelled")) return "destructive";
    return "outline";
  };

  const getCriticalityIcon = (action: string) => {
    const critical = ["deleted", "cancelled", "revoked", "suspended"];
    if (critical.some((term) => action.toLowerCase().includes(term))) {
      return <AlertCircle className="h-4 w-4 text-destructive" />;
    }
    return null;
  };

  if (isMobile) {
    return (
      <div className="space-y-2">
        {activities.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground text-sm">
              {t("activityTable.noneFound")}
            </CardContent>
          </Card>
        ) : (
          activities.map((activity) => {
            const gym = getGymInfo(activity.gym_id);
            return (
              <Card
                key={activity.id}
                className="cursor-pointer hover:bg-accent/50 transition-colors"
                onClick={() => onViewDetails(activity)}
              >
                <CardContent className="p-3">
                  <div className="space-y-2">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {getCriticalityIcon(activity.action)}
                          <Badge variant={getActionBadgeVariant(activity.action)} className="text-xs">
                            {activity.action.replace(/_/g, " ")}
                          </Badge>
                        </div>
                        <p className="font-medium text-sm truncate">
                          {activity.full_name || t("activityTable.notApplicable")}
                        </p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    </div>

                    {/* Target Info */}
                    {activity.class_name ? (
                      <div className="text-xs space-y-0.5">
                        <p className="font-medium text-muted-foreground">{activity.class_name}</p>
                        {activity.booking_date && activity.class_time && (
                          <p className="text-muted-foreground">
                            {format(new Date(activity.booking_date), "dd/MM/yyyy", { locale: fr })} {t("activityTable.at")} {activity.class_time}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground truncate">{activity.target}</p>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-1 border-t">
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(activity.created_at), "dd/MM/yy HH:mm", { locale: fr })}
                      </span>
                      {gym && (
                        <Badge variant="outline" className="text-xs">
                          {gym.name}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("activityTable.date")}</TableHead>
            <TableHead>{t("activityTable.user")}</TableHead>
            <TableHead>{t("activityTable.action")}</TableHead>
            <TableHead>{t("activityTable.target")}</TableHead>
            <TableHead>{t("activityTable.tenant")}</TableHead>
            <TableHead className="text-right">{t("activityTable.actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {activities.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                {t("activityTable.noneFound")}
              </TableCell>
            </TableRow>
          ) : (
            activities.map((activity) => {
              const gym = getGymInfo(activity.gym_id);
              return (
                <TableRow key={activity.id}>
                  <TableCell className="font-mono text-sm">
                    {format(new Date(activity.created_at), "dd/MM/yy HH:mm", { locale: fr })}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getCriticalityIcon(activity.action)}
                      <span className="font-medium">{activity.full_name || t("activityTable.notApplicable")}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getActionBadgeVariant(activity.action)}>
                      {activity.action.replace(/_/g, " ")}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-xs truncate">
                    {activity.class_name && (
                      <div className="text-sm">
                        <div className="font-medium">{activity.class_name}</div>
                        {activity.booking_date && activity.class_time && (
                          <div className="text-muted-foreground">
                            {format(new Date(activity.booking_date), "dd/MM/yyyy", { locale: fr })} {t("activityTable.at")} {activity.class_time}
                          </div>
                        )}
                      </div>
                    )}
                    {!activity.class_name && (
                      <span className="text-sm">{activity.target}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {gym && (
                      <Badge variant="outline" className="font-normal">
                        {gym.name}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onViewDetails(activity)}>
                          <Eye className="h-4 w-4 mr-2" />
                          {t("activityTable.viewDetails")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
